import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { formatZAR, cartLineTotal } from "@/lib/api";
import { CART } from "@/constants/testIds";
import { Plus, Minus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function CartDrawer() {
    const { open, setOpen, lines, updateQty, removeLine, subtotal, clear } = useCart();
    const nav = useNavigate();

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent
                data-testid={CART.drawer}
                side="right"
                className="bg-brand-charcoal border-brand-line text-brand-cream w-full sm:max-w-md p-0 flex flex-col"
            >
                <SheetHeader className="p-6 border-b border-brand-line">
                    <SheetTitle className="font-display text-2xl text-brand-cream">Your Order</SheetTitle>
                    <p className="text-xs text-brand-mute">Pickup at 33 Heronmere Rd, Reuven</p>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {lines.length === 0 ? (
                        <div data-testid={CART.empty} className="text-center py-16 text-brand-mute">
                            <p className="font-script text-brand-gold text-3xl mb-2">Empty</p>
                            <p>Add something delicious to get started.</p>
                        </div>
                    ) : (
                        lines.map((l, i) => (
                            <div key={i} data-testid={`${CART.linePrefix}${i}`} className="border border-brand-line p-4 rounded-sm">
                                <div className="flex justify-between items-start gap-3">
                                    <div>
                                        <p className="font-medium">{l.name}</p>
                                        {(l.modifiers || []).length > 0 && (
                                            <p className="text-xs text-brand-mute mt-1">
                                                {l.modifiers.map((m) => m.name).join(", ")}
                                            </p>
                                        )}
                                    </div>
                                    <button
                                        data-testid={`${CART.removePrefix}${i}`}
                                        onClick={() => removeLine(i)}
                                        className="text-brand-mute hover:text-brand-gold"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                                <div className="flex items-center justify-between mt-3">
                                    <div className="flex items-center gap-2 border border-brand-line rounded-sm">
                                        <button
                                            data-testid={`${CART.decPrefix}${i}`}
                                            className="p-1.5 hover:text-brand-gold"
                                            onClick={() => updateQty(i, l.quantity - 1)}
                                        >
                                            <Minus size={12} />
                                        </button>
                                        <span className="w-6 text-center text-sm">{l.quantity}</span>
                                        <button
                                            data-testid={`${CART.incPrefix}${i}`}
                                            className="p-1.5 hover:text-brand-gold"
                                            onClick={() => updateQty(i, l.quantity + 1)}
                                        >
                                            <Plus size={12} />
                                        </button>
                                    </div>
                                    <span className="text-brand-gold">{formatZAR(cartLineTotal(l))}</span>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {lines.length > 0 && (
                    <div className="p-6 border-t border-brand-line space-y-4">
                        <div className="flex justify-between">
                            <span className="text-sm text-brand-mute">Subtotal</span>
                            <span className="text-lg text-brand-cream">{formatZAR(subtotal)}</span>
                        </div>
                        <p className="text-[11px] text-brand-mute">VAT-inclusive. Pickup only.</p>
                        <div className="flex gap-2">
                            <button
                                data-testid={CART.clearBtn}
                                onClick={clear}
                                className="btn-ghost text-xs"
                            >
                                Clear
                            </button>
                            <button
                                data-testid={CART.checkoutBtn}
                                onClick={() => {
                                    setOpen(false);
                                    nav("/checkout");
                                }}
                                className="btn-primary flex-1"
                            >
                                Checkout · {formatZAR(subtotal)}
                            </button>
                        </div>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
